'use client'

import { BusinessProfileSwitchLoader } from '@/components/BusinessProfile/BusinessProfileSwitchLoader'
import { useGetDefaultBusinessProfileQuery } from '@/lib/features/businessProfile'
import { BusinessProfile } from '@/lib/features/businessProfile/types'
import { createContext, useContext, useEffect, useState } from 'react'

interface BusinessProfileContextType {
  currentProfile: BusinessProfile | null
  isLoading: boolean
  setCurrentProfile: (profile: BusinessProfile | null) => void
  getCurrentBusinessProfileId: () => string | null
  isProfileSwitching: boolean
  setIsProfileSwitching: (switching: boolean) => void
}

const BusinessProfileContext = createContext<BusinessProfileContextType | undefined>(undefined)

export function BusinessProfileProvider({ children }: { children: React.ReactNode }) {
  const [currentProfile, setCurrentProfile] = useState<BusinessProfile | null>(null)
  const [isProfileSwitching, setIsProfileSwitching] = useState(false)
  const { data: defaultProfileResponse, isLoading } = useGetDefaultBusinessProfileQuery()

  // Initialize with default profile when it's loaded
  useEffect(() => {
    if (defaultProfileResponse?.data && !currentProfile) {
      setCurrentProfile(defaultProfileResponse.data)
    }
  }, [defaultProfileResponse?.data, currentProfile])

  const getCurrentBusinessProfileId = () => {
    return currentProfile?.id || null
  }

  const value: BusinessProfileContextType = {
    currentProfile,
    isLoading: isLoading && !currentProfile,
    setCurrentProfile,
    getCurrentBusinessProfileId,
    isProfileSwitching,
    setIsProfileSwitching,
  }

  return (
    <BusinessProfileContext.Provider value={value}>
      {children}
      <BusinessProfileSwitchLoader isVisible={isProfileSwitching} />
    </BusinessProfileContext.Provider>
  )
}

export function useBusinessProfile() {
  const context = useContext(BusinessProfileContext)
  if (context === undefined) {
    throw new Error('useBusinessProfile must be used within a BusinessProfileProvider')
  }
  return context
}

// Hook for getting just the current business profile ID (commonly needed)
export function useCurrentBusinessProfileId() {
  const { getCurrentBusinessProfileId } = useBusinessProfile()
  return getCurrentBusinessProfileId()
}

// Hook for accessing profile switching state
export function useBusinessProfileSwitching() {
  const { isProfileSwitching, setIsProfileSwitching } = useBusinessProfile()
  return { isProfileSwitching, setIsProfileSwitching }
}
